/**
 * Prerequisites verification step
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, XCircle, Loader2, AlertTriangle, Terminal } from 'lucide-react';
import type { PrerequisiteCheck, DeploymentTarget } from '@/lib/deployment/types';

type Props = {
  target: DeploymentTarget;
  blueprintId?: string;
  onComplete: () => void;
  onSkip: () => void;
};

function getPrerequisites(target: DeploymentTarget, blueprintId?: string): PrerequisiteCheck[] {
  const common: PrerequisiteCheck[] = [
    {
      id: 'os',
      name: 'Operating System',
      description: 'Linux/macOS recommended',
      required: false,
      status: 'checking',
    },
    {
      id: 'disk',
      name: 'Disk Space',
      description: 'At least 200GB available',
      required: true,
      status: 'checking',
    },
  ];

  if (target === 'docker-compose') {
    return [
      ...common,
      {
        id: 'docker',
        name: 'Docker',
        description: 'Docker Engine 20.10+',
        required: true,
        status: 'checking',
        command: 'docker --version',
      },
      {
        id: 'docker-compose',
        name: 'Docker Compose',
        description: 'Docker Compose v2.0+',
        required: true,
        status: 'checking',
        command: 'docker compose version',
      },
      {
        id: 'nvidia-docker',
        name: 'NVIDIA Container Toolkit',
        description: 'Required for GPU access',
        required: true,
        status: 'checking',
        command: 'nvidia-smi',
      },
    ];
  }

  if (target === 'kubernetes') {
    return [
      ...common,
      {
        id: 'kubectl',
        name: 'kubectl',
        description: 'Kubernetes CLI tool',
        required: true,
        status: 'checking',
        command: 'kubectl version --client',
      },
      {
        id: 'helm',
        name: 'Helm',
        description: 'Kubernetes package manager',
        required: false,
        status: 'checking',
        command: 'helm version',
      },
    ];
  }

  if (target === 'local' && blueprintId === 'dell-inference-stack') {
    return [
      {
        id: 'terraform',
        name: 'Terraform',
        description: 'Terraform v1.0.0 or higher',
        required: true,
        status: 'checking',
        command: 'terraform --version',
      },
      {
        id: 'network',
        name: 'Network Access',
        description: 'Can reach iDRAC management interface',
        required: true,
        status: 'checking',
      },
      {
        id: 'disk',
        name: 'Disk Space',
        description: 'At least 50GB for deployment files',
        required: true,
        status: 'checking',
      },
    ];
  }

  return common;
}

export function PrerequisitesStep({ target, blueprintId, onComplete, onSkip }: Props) {
  const [checks, setChecks] = useState<PrerequisiteCheck[]>(() => getPrerequisites(target, blueprintId));
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Simulate checking prerequisites
    const timer = setTimeout(() => {
      setChecks(prev =>
        prev.map(check => ({
          ...check,
          status: Math.random() > 0.3 ? 'passed' : check.required ? 'failed' : 'passed',
          errorMessage: Math.random() > 0.3 ? undefined : 'Not found or version too old',
        }))
      );
      setIsChecking(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const allPassed = checks.every(c => c.status === 'passed');
  const requiredFailed = checks.some(c => c.required && c.status === 'failed');

  const getStatusIcon = (status: PrerequisiteCheck['status']) => {
    switch (status) {
      case 'checking':
        return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;
      case 'passed':
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>System Prerequisites</CardTitle>
          <CardDescription>
            Checking your system for required dependencies and tools
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {checks.map((check) => (
            <div
              key={check.id}
              className="flex items-start justify-between p-4 rounded-lg border bg-card"
            >
              <div className="flex items-start gap-3 flex-1">
                {getStatusIcon(check.status)}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{check.name}</span>
                    {check.required && (
                      <Badge variant="outline" className="text-xs">
                        Required
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {check.description}
                  </p>
                  {check.command && (
                    <div className="mt-2 p-2 rounded bg-muted text-xs font-mono flex items-center gap-2">
                      <Terminal className="h-3 w-3" />
                      {check.command}
                    </div>
                  )}
                  {check.status === 'failed' && check.errorMessage && (
                    <Alert variant="destructive" className="mt-2">
                      <AlertDescription className="text-xs">
                        {check.errorMessage}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {!isChecking && (
        <>
          {allPassed && (
            <Alert className="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800 dark:text-green-200">
                All prerequisites are met! You can proceed with deployment.
              </AlertDescription>
            </Alert>
          )}

          {requiredFailed && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>
                Some required prerequisites are not met. Please install the missing dependencies before proceeding.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={onSkip}>
              Skip Checks
            </Button>
            <Button onClick={onComplete} disabled={requiredFailed}>
              Continue
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
